import * as React from 'react';
import { useForm } from 'react-hook-form';
import { Input } from '../../components/Input';
import { SubmitButton } from '../../components/SubmitButton';
import { useAnalytics } from '../../contexts/AnalyticsContext';
import { useTitle } from '../../useTitle';
import {
  useLoginMutation,
  useVerifyMfaMutation,
} from '../../generated/graphql';
import { useAlert } from '../../contexts/AlertContext';
import { paths } from '../../paths';
import { useClient } from '../../contexts/ClientContext';
import { TextLink } from '../../components/ClickableText';
import { LoginLayout } from '../../layouts/LoginLayout';
import { SignUpPrompt } from './SignupPrompt';

interface Fields {
  email: string;
  password: string;
}

export const LogIn: React.FC = () => {
  const analytics = useAnalytics();
  const {
    handleSubmit,
    setError,
    register,
    formState: { errors },
  } = useForm<Fields>({});

  const { resetClient } = useClient();
  const alert = useAlert();
  useTitle('Log in');

  const [loginResponse, sendLogin] = useLoginMutation();

  const submitLogin = async (values: Fields) => {
    analytics.track('Form Submitted', { form: 'login' });

    const { data } = await sendLogin(values);

    if (data?.login?.error?.errorType === 'INVALID_CREDENTIALS') {
      analytics.track('Data Submit Failed', { form: 'login' });
      setError('password', { message: 'Invalid email or password' });
      return;
    }

    if (data?.login?.error?.errorType === 'MFA_NEEDED') {
      analytics.track('Data Submit Succeeded', {
        form: 'login',
        path: 'mfa',
      });
      return;
    }

    if (data?.login?.session) {
      // successfully logged in. The token is set using a cookie so there's no futher work for the form to do
      analytics.track('Data Submit Succeeded', {
        form: 'login',
        path: 'token',
      });
      // reset the client to clear any previously cached resources
      resetClient();
      return;
    }

    alert('Something went wrong', 'danger');
  };

  return (
    <LoginLayout footer={<SignUpPrompt />}>
      {loginResponse.data?.login?.error?.__typename ===
      'MfaNeededLoginError' ? (
        <TotpForm microSession={loginResponse.data.login.error.mfaToken} />
      ) : (
        <form onSubmit={handleSubmit(submitLogin)} className="space-y-6">
          <div className="space-y-4">
            <Input
              label="Email address"
              type="email"
              error={errors.email?.message}
              {...register('email', { required: 'Enter your email address' })}
            />
            <Input
              label="Password"
              type="password"
              error={errors.password?.message}
              {...register('password', { required: 'Enter your password' })}
            />
          </div>

          <SubmitButton loading={loginResponse.fetching}>Log in</SubmitButton>
          <p className="text-center">
            <TextLink to={paths.password.forgot()}>
              Forgot your password?
            </TextLink>
          </p>
        </form>
      )}
    </LoginLayout>
  );
};

const TotpForm = ({ microSession }: { microSession: string }) => {
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<{ code: string }>();
  const [verifyMfaResponse, execute] = useVerifyMfaMutation();
  const { resetClient } = useClient();
  const alert = useAlert();

  const submitOtpCode = async (values: { code: string }) => {
    const { data } = await execute({
      input: { code: values.code, microSession, mfaMethod: 'TOTP' },
    });

    if (data?.verifyMfa?.error?.errorType === 'invalid_code') {
      setError('code', { message: 'Invalid code' });
      return;
    }

    if (data?.verifyMfa?.error?.errorType === 'invalid_session') {
      alert('Login session expired. Please sign in again.', 'warning');
      resetClient();
      return;
    }

    if (data?.verifyMfa?.session) {
      resetClient();
      return;
    }
    alert('Something went wrong', 'danger');
  };

  return (
    <form onSubmit={handleSubmit(submitOtpCode)} className="space-y-6">
      <p>Enter a code from your authenticator device</p>
      <Input
        inputMode="numeric"
        label="OTP Code"
        error={errors.code?.message}
        {...register('code', { required: true })}
      />
      <SubmitButton loading={verifyMfaResponse.fetching}>Log in</SubmitButton>
    </form>
  );
};
